import React, { useState } from 'react';
import { Database, Play, Loader2, AlertCircle, Check, List, ChevronRight, ArrowLeft } from 'lucide-react';
import Modal from '@/UI/CenterScreenModal';

interface ImportDatabaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface DatabaseInfo {
    name: string;
    sizeOnDisk?: number;
    empty?: boolean;
    type?: string;
}

interface ExtractionResult {
    success: boolean;
    classification?: any;
    rawSchema?: any;
    adaptedSchema?: any;
    metadata?: any;
    error?: string;
    details?: string;
}

const ImportDatabaseModal: React.FC<ImportDatabaseModalProps> = ({ isOpen, onClose, onSuccess }) => {
    // Stage: 'connection' | 'selection' | 'details' | 'creation'
    const [stage, setStage] = useState<'connection' | 'selection' | 'details' | 'creation'>('connection');

    // Data State
    const [connectionString, setConnectionString] = useState('');
    const [availableDatabases, setAvailableDatabases] = useState<DatabaseInfo[]>([]);
    const [selectedDatabase, setSelectedDatabase] = useState<string | null>(null);
    const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
    const [projectName, setProjectName] = useState('');
    const [projectDescription, setProjectDescription] = useState('');

    // Loading & Error State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const resetState = () => {
        setStage('connection');
        setConnectionString('');
        setAvailableDatabases([]);
        setSelectedDatabase(null);
        setExtractionResult(null);
        setProjectName('');
        setProjectDescription('');
        setError('');
        setLoading(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Step 1: List Databases
    const handleListDatabases = async () => {
        if (!connectionString.trim()) {
            setError('Please enter a connection string');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/database/list', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connectionString }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to list databases');
            }

            if (data.requiresSelection && data.databases.length > 1) {
                setAvailableDatabases(data.databases);
                setStage('selection');
            } else {
                // Single DB or no selection needed, proceed to extract
                await handleExtract(null);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to connect to database');
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Extract Schema
    const handleExtract = async (dbName: string | null) => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/database/extract', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    connectionString,
                    selectedDatabase: dbName || undefined,
                    sampleSize: 100 // Default sample size
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to extract schema');
            }

            setExtractionResult(data);

            // Auto-generate project name from DB name if possible
            const defaultName = dbName ||
                (data.classification?.type ? `${data.classification.type} Project` : 'Imported Project');
            setProjectName(defaultName);

            setStage('creation');
        } catch (err: any) {
            setError(err.message || 'Failed to extract schema');
            // If we were in selection mode, stay there, otherwise go back to connection
            if (stage !== 'selection') {
                setStage('connection');
            }
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Create Project
    const handleCreateProject = async () => {
        if (!projectName.trim()) {
            setError('Project name is required');
            return;
        }

        setLoading(true);
        setError('');

        // Prepare payload matching the standard project creation structure
        // See src/app/api/projects/route.ts and src/lib/validation.ts
        const payload = {
            name: projectName,
            description: projectDescription,
            models: extractionResult?.adaptedSchema || [],
            metadata: {
                databaseType: extractionResult?.classification?.type || 'MongoDB',
                authentication: false,
                authenticationMethods: {
                    emailPassword: true,
                    googleAuth: false
                },
                fileStorage: false,
                rateLimiterEnabled: false,
                corsEnabled: false,
                websocketEnabled: false
            },
            tasks: [], // Initialize with empty tasks array
            code: "",
            srs: ""
        };

        console.log('Creating project with payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await fetch('/api/projects', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const data = await response.json();
                console.error('Project creation failed:', data);
                throw new Error(data.error || 'Failed to create project');
            }

            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('Error creating project:', err);
            setError(err.message || 'Failed to create project');
        } finally {
            setLoading(false);
        }
    };

    const renderConnectionStage = () => (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-[var(--primaryText)] mb-2">
                    Database Connection String
                </label>
                <input
                    type="text"
                    value={connectionString}
                    onChange={(e) => setConnectionString(e.target.value)}
                    placeholder="mongodb://... or postgresql://..."
                    className="w-full px-4 py-3 bg-[var(--primaryBackground)] border border-[var(--primaryBorder)] rounded-lg text-[var(--primaryText)] placeholder-[var(--secondaryText)] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
                <p className="text-xs text-[var(--secondaryText)] mt-2">
                    Supports MongoDB, PostgreSQL, MySQL, SQLite, and more.
                </p>
            </div>

            <button
                onClick={handleListDatabases}
                disabled={loading || !connectionString.trim()}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
            >
                {loading ? (
                    <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Connecting...
                    </>
                ) : (
                    <>
                        <Database className="w-5 h-5" />
                        Connect & Analyze
                    </>
                )}
            </button>
        </div>
    );

    const renderSelectionStage = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-[var(--primaryText)]">Select Database</h3>
                <button
                    onClick={() => setStage('connection')}
                    className="text-sm text-[var(--secondaryText)] hover:text-[var(--primaryText)] flex items-center gap-1"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
            </div>

            <div className="grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto pr-2">
                {availableDatabases.map((db) => (
                    <button
                        key={db.name}
                        onClick={() => handleExtract(db.name)}
                        disabled={loading}
                        className="p-4 bg-[var(--primaryBackground)] hover:bg-blue-600/10 border border-[var(--primaryBorder)] hover:border-blue-500 rounded-lg text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed group flex items-center justify-between"
                    >
                        <div>
                            <div className="font-semibold text-[var(--primaryText)] group-hover:text-blue-500 transition-colors">
                                {db.name}
                            </div>
                            <div className="flex gap-3 mt-1">
                                {db.type && (
                                    <span className="text-xs text-[var(--secondaryText)] bg-[var(--hoverBackground)] px-2 py-0.5 rounded">
                                        {db.type}
                                    </span>
                                )}
                                {db.sizeOnDisk !== undefined && (
                                    <span className="text-xs text-[var(--secondaryText)]">
                                        {formatBytes(db.sizeOnDisk)}
                                    </span>
                                )}
                            </div>
                        </div>
                        {loading ? (
                            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                        ) : (
                            <ChevronRight className="w-5 h-5 text-[var(--secondaryText)] group-hover:text-blue-500" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderCreationStage = () => (
        <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <div>
                        <h4 className="font-medium text-green-500">Schema Extracted Successfully</h4>
                        <p className="text-sm text-green-400/80">
                            Found {extractionResult?.adaptedSchema?.length || 0} models
                            from {extractionResult?.classification?.type}
                        </p>
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-[var(--primaryText)] mb-2">
                    Project Name
                </label>
                <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="w-full px-4 py-3 bg-[var(--primaryBackground)] border border-[var(--primaryBorder)] rounded-lg text-[var(--primaryText)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="My Awesome Project"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-[var(--primaryText)] mb-2">
                    Description (Optional)
                </label>
                <textarea
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    className="w-full px-4 py-3 bg-[var(--primaryBackground)] border border-[var(--primaryBorder)] rounded-lg text-[var(--primaryText)] focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                    placeholder="Describe your project..."
                />
            </div>

            <div className="flex gap-3 pt-2">
                <button
                    onClick={() => setStage('connection')} // Or selection if that was previous
                    className="flex-1 px-6 py-3 bg-[var(--hoverBackground)] hover:bg-[var(--primaryBorder)] text-[var(--primaryText)] rounded-lg font-medium transition-all"
                >
                    Back
                </button>
                <button
                    onClick={handleCreateProject}
                    disabled={loading || !projectName.trim()}
                    className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Creating...
                        </>
                    ) : (
                        <>
                            <Play className="w-5 h-5" />
                            Create Project
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Import from Database">
            <div className="p-1">
                {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-400">{error}</p>
                    </div>
                )}

                {stage === 'connection' && renderConnectionStage()}
                {stage === 'selection' && renderSelectionStage()}
                {stage === 'creation' && renderCreationStage()}
            </div>
        </Modal>
    );
};

export default ImportDatabaseModal;
